import React, { useState, useEffect, useCallback } from 'react'
import { Card, Row, Col, Statistic, Table, Button, Tooltip, message as antMessage, Spin } from 'antd'
import { ReloadOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import api from '../api'
import DonutChart from './analytics/DonutChart'

const CHART_COLORS = {
  kuaishou: '#fa8c16',
  bilibili: '#a0d911',
  shipinhao: '#52c41a',
  douyin: '#237804',
  xiaohongshu: '#13c2c2',
  weibo: '#eb2f96',
  jinritoutiao: '#2f54eb'
}

function fmtOrDash(value) {
  if (value === null || value === undefined || value === 0) return '-'
  return value.toLocaleString()
}

function formatLastUpdated(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const d = await api.getAnalyticsOverview()
      setData(d)
    } catch (err) {
      antMessage.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const platformStats = data?.platformStats || []
  const y = data?.yesterdayMetrics || {}

  const rankColumns = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 60, align: 'center',
      render: (_, __, idx) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 24, height: 24, borderRadius: '50%',
          background: idx < 3 ? ['#ffd700', '#c0c0c0', '#cd7f32'][idx] : '#f0f0f0',
          color: idx < 3 ? '#fff' : '#666', fontWeight: 600, fontSize: 12
        }}>
          {idx + 1}
        </span>
      )
    },
    { title: '平台', dataIndex: 'name', key: 'name', width: 100,
      render: (name, record) => (
        <span>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: CHART_COLORS[record.platform] || '#999', marginRight: 6 }} />
          {name}
        </span>
      )
    },
    { title: '账号总数', dataIndex: 'accountCount', key: 'accountCount', width: 90, align: 'center',
      sorter: (a, b) => a.accountCount - b.accountCount
    },
    { title: '播放总数', dataIndex: 'playCount', key: 'playCount', width: 100, align: 'center',
      render: v => fmtOrDash(v),
      sorter: (a, b) => (a.playCount || 0) - (b.playCount || 0)
    },
    { title: '粉丝总数', dataIndex: 'fanCount', key: 'fanCount', width: 100, align: 'center',
      render: v => v ? v.toLocaleString() : '-',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.fanCount - b.fanCount
    }
  ]

  const playDonutData = platformStats
    .filter(p => p.accountCount > 0)
    .map(p => ({ label: p.name, value: p.playCount || 0, color: CHART_COLORS[p.platform] || '#999' }))

  const fanDonutData = platformStats
    .filter(p => p.fanCount > 0)
    .map(p => ({ label: p.name, value: p.fanCount, color: CHART_COLORS[p.platform] || '#999' }))

  const workDonutData = platformStats
    .filter(p => p.workCount > 0)
    .map(p => ({ label: p.name, value: p.workCount, color: CHART_COLORS[p.platform] || '#999' }))

  const cardStyle = { background: '#fff', borderRadius: 6, border: '1px solid #f0f0f0', padding: '16px 20px' }

  return (
    <div style={{ flex: 1, height: '100%', overflow: 'auto', background: '#f5f6fa', padding: 20 }}>
      <Spin spinning={loading}>
        {/* ---- Header ---- */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1a1a1a' }}>多平台总览</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: '#999' }}>
              最后更新：{data ? formatLastUpdated(data.lastUpdated) : '--'}
            </span>
            <Tooltip title="数据每60秒自动刷新">
              <QuestionCircleOutlined style={{ color: '#bbb', fontSize: 14, cursor: 'help' }} />
            </Tooltip>
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}
              style={{ background: '#fa8c16', borderColor: '#fa8c16' }}>
              刷新数据
            </Button>
          </div>
        </div>

        {/* ---- Metric Cards (2 rows × 4 cols) ---- */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="昨日新增播放数" value={fmtOrDash(y.newPlays)} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="作品总数" value={data?.totalWorks ?? 0} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="昨日净增粉" value={fmtOrDash(y.newFans)} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic
                title="粉丝总数"
                value={data?.totalFans ?? 0}
                valueStyle={{ fontSize: 28 }}
                suffix={<span style={{ fontSize: 13, color: '#999', fontWeight: 400, marginLeft: 6 }}>账号总数：{data?.totalAccounts ?? 0}</span>}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="昨日新增点赞数" value={fmtOrDash(y.newLikes)} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="昨日新增收藏数" value={fmtOrDash(y.newFavorites)} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="昨日新增评论数" value={fmtOrDash(y.newComments)} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 6 }}>
              <Statistic title="昨日新增转发数" value={fmtOrDash(y.newShares)} valueStyle={{ fontSize: 28 }} />
            </Card>
          </Col>
        </Row>

        {/* ---- Platform Ranking Table + 播放总数分布 ---- */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={14}>
            <Card
              title={<span style={{ fontSize: 16, fontWeight: 600 }}>平台排名</span>}
              styles={{ body: { padding: '12px 16px' } }}
              style={{ borderRadius: 6, height: '100%' }}
            >
              <Table
                columns={rankColumns}
                dataSource={platformStats.map((p, i) => ({ ...p, key: p.platform, rank: i + 1 }))}
                pagination={false}
                size="small"
                showSorterTooltip={false}
              />
            </Card>
          </Col>
          <Col span={10}>
            <DonutChart title="播放总数分布" data={playDonutData} size={200} />
          </Col>
        </Row>

        {/* ---- 粉丝总数分布 + 视频作品总数分布 ---- */}
        <Row gutter={16}>
          <Col span={12}>
            <DonutChart title="粉丝总数分布" data={fanDonutData} size={220} />
          </Col>
          <Col span={12}>
            <DonutChart title="视频作品总数分布" data={workDonutData} size={220} />
          </Col>
        </Row>
      </Spin>
    </div>
  )
}
